<template>
  <TransitionRoot appear :show="isVisible" as="template">
    <div>
      <TransitionChild
        enter="transform ease-out duration-300 transition"
        enter-from="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to="translate-y-0 opacity-100 sm:translate-x-0"
        leave="transition ease-in duration-100"
        leave-from="opacity-100"
        leave-to="opacity-0"
        class="max-w-3xl w-full bg-white dark:bg-secondary-800 shadow-lg rounded-lg pointer-events-auto ring-1 ring-black ring-opacity-5 mb-4"
      >
        <div class="p-4">
          <div class="flex items-start">
            <div class="flex-shrink-0">
              <CheckCircleIcon v-if="type === 'success'" class="h-6 w-6 text-green-400" />
              <ExclamationCircleIcon v-else class="h-6 w-6 text-red-400" />
            </div>
            <div class="ml-3 w-full flex-1 pt-0.5">
              <p class="text-sm font-medium text-secondary-900 dark:text-white">
                {{ title }}
              </p>
              <p class="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
                {{ message }}
              </p>
            </div>
            <div class="ml-4 flex-shrink-0 flex">
              <button
                class="bg-white dark:bg-secondary-800 rounded-md inline-flex text-secondary-400 dark:text-secondary-500 hover:text-secondary-500 dark:hover:text-secondary-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                @click="close"
              >
                <span class="sr-only">Cerrar</span>
                <XMarkIcon class="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
        
        <!-- Barra de progreso -->
        <div 
          class="h-1 bg-primary-100 dark:bg-primary-900/30 rounded-b-lg overflow-hidden"
          v-if="autoClose"
        >
          <div 
            class="h-full bg-primary-500 transition-all duration-300 ease-in-out"
            :style="{ width: `${progressWidth}%` }"
          ></div>
        </div>
      </TransitionChild>
    </div>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue'
import { TransitionRoot, TransitionChild } from '@headlessui/vue'
import { CheckCircleIcon, ExclamationCircleIcon, XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps<{
  type: 'success' | 'error'
  title: string
  message: string
  autoClose?: boolean
  duration?: number
}>()

const emit = defineEmits(['close'])

const isVisible = ref(true)
const progressWidth = ref(100)
let timer: NodeJS.Timeout | null = null
let progressTimer: NodeJS.Timeout | null = null

const close = () => {
  isVisible.value = false
  if (timer) clearTimeout(timer)
  if (progressTimer) clearInterval(progressTimer)
  emit('close')
}

onMounted(() => {
  if (props.autoClose) {
    const duration = props.duration || 5000
    const progressInterval = 10 // Actualizar cada 10ms
    const steps = duration / progressInterval
    const progressStep = 100 / steps

    progressTimer = setInterval(() => {
      progressWidth.value = Math.max(0, progressWidth.value - progressStep)
    }, progressInterval)

    timer = setTimeout(close, duration)
  }
})

onBeforeUnmount(() => {
  if (timer) clearTimeout(timer)
  if (progressTimer) clearInterval(progressTimer)
})
</script> 